import { useEffect } from 'react';

const SITE_NAME = 'AURABLOX';
const SITE_URL = 'https://www.aurablox.com';
const DEFAULT_IMAGE = `${SITE_URL}/images/og-aurablox.png`;
const DEFAULT_TWITTER_IMAGE = `${SITE_URL}/images/og-aurablox.png`;

const upsertMeta = (key, value, content) => {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${key}="${value}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(key, value);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
};

const upsertCanonical = (href) => {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const upsertJsonLd = (id, data) => {
  if (!data) return;
  let el = document.head.querySelector(`script[type="application/ld+json"][data-schema="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-schema', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const getSiteSchema = () => [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/aurablox-logo-mark.png`,
    image: DEFAULT_IMAGE,
    email: 'jjohnson@aurablox.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '6 Cardinal Way',
      addressLocality: 'St. Louis',
      addressRegion: 'MO',
      postalCode: '63102',
      addressCountry: 'US',
    },
    sameAs: [
      'https://www.linkedin.com/in/jjohnso75/',
      'https://github.com/murderszn',
      'https://x.com/jahflyx',
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: SITE_URL,
    publisher: {
      '@id': `${SITE_URL}/#organization`,
    },
  },
];

const SEO = ({
  title,
  description,
  path = '',
  canonicalUrl,
  type = 'website',
  image = DEFAULT_IMAGE,
  twitterImage = DEFAULT_TWITTER_IMAGE,
  noindex = false,
  schema,
}) => {
  useEffect(() => {
    const pageTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const url = canonicalUrl || `${SITE_URL}${path}`;
    document.title = pageTitle;

    upsertMeta('name', 'title', pageTitle);
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');

    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:title', pageTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    upsertMeta('property', 'og:image:alt', `${SITE_NAME} social preview`);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', pageTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', twitterImage || image);
    upsertMeta('name', 'twitter:image:alt', `${SITE_NAME} social preview`);

    upsertCanonical(url);
    upsertJsonLd('site', getSiteSchema());
    upsertJsonLd('page', schema);
  }, [title, description, path, canonicalUrl, type, image, twitterImage, noindex, schema]);

  return null;
};

export default SEO;
